/**
 * Shop the Look — modal launcher + AJAX add-to-cart.
 * Each look card has a <script type="application/json" data-stl-data="{look_id}">
 * with its image + product list. On CTA click, we populate a single modal per
 * section and show it. "AÑADIR" posts /cart/add.js and updates the cart bubble.
 */
(function () {
  'use strict';

  // The script can be loaded twice when both the regular STL section and the
  // STL-influencers section render on the same page (each emits its own <script>
  // tag). Bail out on the second pass so we don't bind every listener twice.
  if (window.__iuropStlInit) return;
  window.__iuropStlInit = true;

  const moneyFormat = (cents) => {
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `€${amount}`;
  };

  // ------------------------------------------------------------------
  // Body scroll lock — `overflow: hidden` alone doesn't stop iOS Safari
  // from scrolling. The reliable trick is to pin the body with
  // position: fixed and restore the scroll position on unlock. A counter
  // handles two modals being open at once across STL + influencers.
  // ------------------------------------------------------------------
  let bodyLockCount = 0;
  let bodyLockScrollY = 0;
  const lockBody = () => {
    if (bodyLockCount === 0) {
      bodyLockScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${bodyLockScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    bodyLockCount++;
  };
  const unlockBody = () => {
    if (bodyLockCount === 0) return;
    bodyLockCount--;
    if (bodyLockCount === 0) {
      // Temporarily disable smooth scrolling (set globally in base.css) so
      // the position restore is instantaneous — otherwise the page first
      // snaps to top (when position:fixed is removed) and then smoothly
      // animates back down, which looks like "the page jumped to the top".
      const htmlEl = document.documentElement;
      const prevScrollBehavior = htmlEl.style.scrollBehavior;
      htmlEl.style.scrollBehavior = 'auto';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, bodyLockScrollY);
      htmlEl.style.scrollBehavior = prevScrollBehavior;
    }
  };

  // ------------------------------------------------------------------
  // Drag-to-scroll for the horizontal row on touch + mouse devices.
  // ------------------------------------------------------------------
  const initDragScroll = (row) => {
    if (!row) return;
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    const DRAG_THRESHOLD = 4; // px before treating as drag

    const onPointerDown = (e) => {
      // Only react to primary button / pen — let touch use native scroll-snap
      if (e.pointerType === 'touch') return;
      if (e.button !== undefined && e.button !== 0) return;
      // Don't hijack pointers that started on an interactive control
      // (CTA button, links). setPointerCapture on the row would otherwise
      // swallow the click on the descendant button.
      if (e.target.closest('button, a, [data-stl-open]')) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = row.scrollLeft;
    };

    const onPointerMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        row.classList.add('is-dragging');
        try { row.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (moved) row.scrollLeft = startScrollLeft - dx;
    };

    const end = (e) => {
      if (!isDown) return;
      isDown = false;
      row.classList.remove('is-dragging');
      try { row.releasePointerCapture(e.pointerId); } catch (_) {}
    };

    // If the user dragged, swallow the click on children (so cards don't open).
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    row.addEventListener('pointerdown', onPointerDown);
    row.addEventListener('pointermove', onPointerMove);
    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);
    row.addEventListener('pointerleave', end);
    row.addEventListener('click', onClickCapture, true);
  };

  const initSection = (section) => {
    if (section.dataset.stlInit === '1') return;
    section.dataset.stlInit = '1';

    const modal = section.querySelector('[data-stl-modal]');
    if (!modal) return;

    // Enable horizontal drag/swipe on the cards row.
    initDragScroll(section.querySelector('.iurop-stl__row'));

    const modalImage = modal.querySelector('[data-stl-modal-image]');
    const modalHandle = modal.querySelector('[data-stl-modal-handle]');
    const modalProducts = modal.querySelector('[data-stl-modal-products]');
    const progress = modal.querySelector('[data-stl-progress]');
    const progressThumb = modal.querySelector('[data-stl-progress-thumb]');
    const addAllBtn = modal.querySelector('[data-stl-add-all]');
    const feedback = modal.querySelector('[data-stl-feedback]');

    const updateProgress = () => {
      if (!progress || !progressThumb || !modalProducts) return;
      const max = modalProducts.scrollWidth - modalProducts.clientWidth;
      if (max <= 1) {
        progress.style.display = 'none';
        return;
      }
      progress.style.display = '';
      const ratio = Math.min(1, Math.max(0, modalProducts.scrollLeft / max));
      const trackWidth = progress.clientWidth;
      const thumbWidth = progressThumb.offsetWidth;
      const travel = trackWidth - thumbWidth;
      progressThumb.style.transform = `translateX(${travel * ratio}px)`;
    };

    if (modalProducts) {
      modalProducts.addEventListener('scroll', updateProgress, { passive: true });
      window.addEventListener('resize', updateProgress);
    }
    let lastFocused = null;
    let currentProducts = [];

    // Pending = available products with chips whose variant the user
    // hasn't picked yet.
    const getPendingProducts = () =>
      currentProducts.filter(
        (p) => p.available && Array.isArray(p.variants) && p.variants.length > 1 && !p.selected_variant_id
      );

    const pendingMessage = (pending) => {
      const name = pending[0] && pending[0].option_name ? pending[0].option_name.toLowerCase() : 'opción';
      if (pending.length === 1) return `Te falta elegir ${name}`;
      return `Te faltan ${pending.length} ${name}s por elegir`;
    };

    // Disables "Añadir todo" while any product with inline options still
    // needs a variant. Uses aria-disabled (not the native disabled attr)
    // so the button still receives clicks and can surface a tooltip.
    const updateAddAllState = () => {
      if (!addAllBtn || addAllBtn.hidden) return;
      const pending = getPendingProducts();
      const isPending = pending.length > 0;
      addAllBtn.setAttribute('aria-disabled', isPending ? 'true' : 'false');
      if (!isPending) hideAddAllTooltip();
    };

    let addAllTooltipEl = null;
    let addAllTooltipTimer = 0;
    const ensureTooltipEl = () => {
      if (addAllTooltipEl) return addAllTooltipEl;
      // Wrap the button in a positioning context so the tooltip can be
      // anchored to it without disturbing layout.
      if (!addAllBtn.parentElement.classList.contains('iurop-stl-modal__add-all-wrap')) {
        const w = document.createElement('div');
        w.className = 'iurop-stl-modal__add-all-wrap';
        addAllBtn.parentElement.insertBefore(w, addAllBtn);
        w.appendChild(addAllBtn);
      }
      addAllTooltipEl = document.createElement('div');
      addAllTooltipEl.className = 'iurop-stl-modal__tooltip';
      addAllTooltipEl.setAttribute('role', 'tooltip');
      addAllBtn.parentElement.insertBefore(addAllTooltipEl, addAllBtn);
      return addAllTooltipEl;
    };
    const showAddAllTooltip = (message) => {
      const tip = ensureTooltipEl();
      tip.textContent = message;
      tip.classList.add('is-visible');
      // Auto-hide on touch (where there's no hover-out) so the tip doesn't
      // linger forever after a tap.
      window.clearTimeout(addAllTooltipTimer);
      addAllTooltipTimer = window.setTimeout(() => tip.classList.remove('is-visible'), 3500);
    };
    const hideAddAllTooltip = () => {
      if (!addAllTooltipEl) return;
      addAllTooltipEl.classList.remove('is-visible');
      window.clearTimeout(addAllTooltipTimer);
    };

    const open = (lookId) => {
      const dataNode = section.querySelector(`script[data-stl-data="${lookId}"]`);
      if (!dataNode) return;

      let data;
      try {
        data = JSON.parse(dataNode.textContent);
      } catch (err) {
        console.error('[iurop-stl] Invalid JSON for look', lookId, err);
        return;
      }

      // Populate image
      if (data.image) {
        modalImage.src = data.image;
        modalImage.alt = data.image_alt || '';
        modalImage.style.display = '';
      } else {
        modalImage.removeAttribute('src');
        modalImage.style.display = 'none';
      }

      // Populate handle (only if the modal has a placeholder for it)
      if (modalHandle) {
        if (data.handle) {
          modalHandle.textContent = data.handle;
          modalHandle.style.display = '';
        } else {
          modalHandle.textContent = '';
          modalHandle.style.display = 'none';
        }
      }

      // Populate product list
      currentProducts = Array.isArray(data.products) ? data.products : [];
      modalProducts.innerHTML = '';
      if (Array.isArray(data.products) && data.products.length) {
        data.products.forEach((product, idx) => {
          try {
          const li = document.createElement('li');
          li.className = 'iurop-stl-modal__product';

          const img = document.createElement('img');
          img.className = 'iurop-stl-modal__product-img';
          img.src = product.image || '';
          img.alt = product.title || '';
          img.loading = 'lazy';
          li.appendChild(img);

          const info = document.createElement('div');
          info.className = 'iurop-stl-modal__product-info';

          const titleLink = document.createElement('a');
          titleLink.className = 'iurop-stl-modal__product-title-link';
          titleLink.href = product.url || '#';

          const title = document.createElement('p');
          title.className = 'iurop-stl-modal__product-title';
          title.textContent = product.title || '';
          titleLink.appendChild(title);

          const price = document.createElement('span');
          price.className = 'iurop-stl-modal__product-price';
          price.textContent = product.price || '';

          info.appendChild(titleLink);
          info.appendChild(price);

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'iurop-stl-modal__product-add';
          const tStl = (window.theme && window.theme.t && window.theme.t.shopTheLook) || {};
          btn.textContent = product.available ? (tStl.add || 'Añadir') : (tStl.soldOut || 'Agotado');
          btn.disabled = !product.available;

          // Inline option picker (e.g. sizes) for products with a single
          // option dimension. Multi-option products (size + color) still
          // redirect to the PDP.
          const hasInlineOptions = Array.isArray(product.variants) && product.variants.length > 1;
          product.selected_variant_id = null;

          if (hasInlineOptions) {
            const opts = document.createElement('div');
            opts.className = 'iurop-stl-modal__product-options';
            opts.setAttribute('role', 'group');
            if (product.option_name) opts.setAttribute('aria-label', product.option_name);

            product.variants.forEach((v) => {
              const chip = document.createElement('button');
              chip.type = 'button';
              chip.className = 'iurop-stl-modal__chip';
              chip.textContent = v.label;
              chip.dataset.variantId = v.id;
              chip.disabled = !v.available;
              if (!v.available) chip.classList.add('is-unavailable');
              chip.addEventListener('click', () => {
                opts.querySelectorAll('.iurop-stl-modal__chip').forEach((c) => c.classList.remove('is-selected'));
                chip.classList.add('is-selected');
                product.selected_variant_id = v.id;
                btn.disabled = false;
                updateAddAllState();
              });
              opts.appendChild(chip);
            });

            info.appendChild(opts);
            // Require explicit selection before enabling the button.
            btn.disabled = true;
          }

          li.appendChild(info);
          btn.addEventListener('click', () => {
            if (hasInlineOptions) {
              if (!product.selected_variant_id) {
                if (product.option_name) {
                  feedback.textContent = (tStl.chooseX || 'Elige [option].').replace('[option]', product.option_name.toLowerCase());
                } else {
                  feedback.textContent = (tStl.chooseOption || 'Elige una opción.');
                }
                return;
              }
              handleAdd(btn, product, product.selected_variant_id);
            } else {
              handleAdd(btn, product);
            }
          });
          li.appendChild(btn);

          modalProducts.appendChild(li);
          } catch (err) {
            console.error('[iurop-stl] failed to render product', idx, product, err);
          }
        });
      } else {
        const tStlEmpty = (window.theme && window.theme.t && window.theme.t.shopTheLook) || {};
        const empty = document.createElement('li');
        empty.className = 'iurop-stl-modal__product';
        empty.textContent = tStlEmpty.noProducts || 'No hay productos configurados para este look.';
        modalProducts.appendChild(empty);
      }

      // Show "Añadir todo" if at least 2 items are present and at least one
      // can be resolved without leaving the modal (single-variant or single-
      // option items where the user can pick inline).
      if (addAllBtn) {
        const resolvable = currentProducts.filter(
          (p) => p.available && (p.has_only_default_variant || (Array.isArray(p.variants) && p.variants.length > 1))
        );
        if (resolvable.length >= 2) {
          addAllBtn.hidden = false;
          addAllBtn.classList.remove('is-loading', 'is-added');
          addAllBtn.textContent = addAllBtn.dataset.originalText || addAllBtn.textContent;
        } else {
          addAllBtn.hidden = true;
        }
        updateAddAllState();
      }

      feedback.textContent = '';
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('stl-modal-open');
      lockBody();

      if (modalProducts) modalProducts.scrollLeft = 0;
      window.requestAnimationFrame(updateProgress);

      const firstBtn = modal.querySelector('[data-stl-close]');
      if (firstBtn) firstBtn.focus({ preventScroll: true });
    };

    const close = () => {
      if (!modal.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('stl-modal-open');
      unlockBody();
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    };

    const handleAdd = (btn, product, variantId) => {
      // Multi-option product without a resolved variant → send to PDP.
      if (!variantId && !product.has_only_default_variant) {
        window.location.href = product.url;
        return;
      }
      const tStlAdd = (window.theme && window.theme.t && window.theme.t.shopTheLook) || {};
      const id = variantId || product.variant_id;
      if (!id) {
        feedback.textContent = tStlAdd.variantUnavailable || 'Variante no disponible.';
        return;
      }

      btn.classList.add('is-loading');
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = '…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({ id: id, quantity: 1 }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(err));
          return res.json();
        })
        .then(() => fetch('/cart.js').then((r) => r.json()))
        .then((cart) => {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          btn.textContent = tStlAdd.added || 'Añadido';
          feedback.textContent = (tStlAdd.productAdded || '[product] añadido al carrito.').replace('[product]', product.title);
          updateCartCount(cart.item_count);
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart } })
          );

          window.setTimeout(() => {
            btn.classList.remove('is-added');
            btn.textContent = originalText;
            btn.disabled = false;
          }, 1800);
        })
        .catch((err) => {
          console.error('[iurop-stl] add-to-cart failed', err);
          btn.classList.remove('is-loading');
          btn.textContent = originalText;
          btn.disabled = false;
          feedback.textContent = (err && err.description) || tStlAdd.addFailed || 'No se ha podido añadir. Intenta de nuevo.';
        });
    };

    const handleAddAll = () => {
      const tStlAll = (window.theme && window.theme.t && window.theme.t.shopTheLook) || {};
      if (!addAllBtn) return;
      // If blocked because some sizes are still pending, show the tooltip
      // explaining what to do and bail out.
      if (addAllBtn.getAttribute('aria-disabled') === 'true') {
        const pending = getPendingProducts();
        if (pending.length > 0) {
          showAddAllTooltip(pendingMessage(pending));
          return;
        }
      }

      // Resolved variant per product:
      //   - single-variant products → product.variant_id
      //   - inline-options products → only if the user picked a chip
      const addable = currentProducts
        .filter((p) => p.available)
        .map((p) => {
          if (p.has_only_default_variant && p.variant_id) {
            return { product: p, variantId: p.variant_id };
          }
          if (Array.isArray(p.variants) && p.selected_variant_id) {
            return { product: p, variantId: p.selected_variant_id };
          }
          return null;
        })
        .filter(Boolean);

      if (!addable.length) {
        feedback.textContent = tStlAll.chooseOptions || 'Elige las opciones de los productos antes de añadir.';
        return;
      }

      const skipped = currentProducts.length - addable.length;
      const originalText = addAllBtn.dataset.originalText || addAllBtn.textContent;
      addAllBtn.dataset.originalText = originalText;
      addAllBtn.classList.add('is-loading');
      addAllBtn.disabled = true;
      addAllBtn.textContent = '…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({
          items: addable.map((entry) => ({ id: entry.variantId, quantity: 1 })),
        }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(err));
          return res.json();
        })
        .then(() => fetch('/cart.js').then((r) => r.json()))
        .then((cart) => {
          addAllBtn.classList.remove('is-loading');
          addAllBtn.classList.add('is-added');
          addAllBtn.textContent = (tStlAll.addedCount || 'Añadido ([count])').replace('[count]', addable.length);
          feedback.textContent = skipped > 0
            ? (tStlAll.addedSkipped || '[added] productos añadidos. [skipped] requieren elegir variante.').replace('[added]', addable.length).replace('[skipped]', skipped)
            : (tStlAll.addedSummary || '[count] productos añadidos al carrito.').replace('[count]', addable.length);
          updateCartCount(cart.item_count);
          // Close the modal first so the cart drawer (opened by the
          // cart:updated listener) is visible — otherwise the user can't
          // tell anything was added.
          close();
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart } })
          );

          window.setTimeout(() => {
            addAllBtn.classList.remove('is-added');
            addAllBtn.textContent = originalText;
            addAllBtn.disabled = false;
          }, 2000);
        })
        .catch((err) => {
          console.error('[iurop-stl] add-all failed', err);
          addAllBtn.classList.remove('is-loading');
          addAllBtn.textContent = originalText;
          addAllBtn.disabled = false;
          feedback.textContent = (err && err.description) || tStlAll.addFailed || 'No se ha podido añadir. Intenta de nuevo.';
        });
    };

    const updateCartCount = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = count;
        node.classList.toggle('visually-hidden', count === 0);
      });
    };

    // Expose the opener — the window-level capture-phase delegated click
    // handler at the bottom of this file is the single source of truth for
    // CTA clicks (it intercepts them BEFORE third-party embeds like Klaviyo
    // or A/B testing tools can hijack the event).
    section.__stlOpen = open;

    modal.querySelectorAll('[data-stl-close]').forEach((c) =>
      c.addEventListener('click', close)
    );

    if (addAllBtn) {
      addAllBtn.addEventListener('click', handleAddAll);
      // Show the tooltip on hover when the button is blocked.
      addAllBtn.addEventListener('mouseenter', () => {
        if (addAllBtn.getAttribute('aria-disabled') !== 'true') return;
        const pending = getPendingProducts();
        if (pending.length > 0) showAddAllTooltip(pendingMessage(pending));
      });
      addAllBtn.addEventListener('mouseleave', hideAddAllTooltip);
      addAllBtn.addEventListener('focus', () => {
        if (addAllBtn.getAttribute('aria-disabled') !== 'true') return;
        const pending = getPendingProducts();
        if (pending.length > 0) showAddAllTooltip(pendingMessage(pending));
      });
      addAllBtn.addEventListener('blur', hideAddAllTooltip);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-stl-section]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on theme editor reload.
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-stl-section]');
    if (section) initSection(section);
  });

  // ------------------------------------------------------------------
  // Multi-event delegated trigger.
  //
  // Why three events instead of just `click`?
  // Klaviyo's onsite embed (and similar marketing apps) attach capture-phase
  // click listeners on document that swallow clicks — so a plain `click`
  // handler may never fire. By listening for `pointerdown` (fires BEFORE
  // click, almost never intercepted) and `keydown` (Enter / Space, for
  // keyboard users), we open the modal in the first event the user
  // produces, regardless of who's racing for `click` later.
  // ------------------------------------------------------------------
  let lastTriggerAt = 0;
  const triggerOpen = (e, btn) => {
    // Dedupe — pointerdown + click + keydown can all fire for the same
    // user action; only open once per 500ms window.
    const now = Date.now();
    if (now - lastTriggerAt < 500) return;
    // Find the section. Prefer the data attribute, but fall back to the
    // CSS class so we survive old liquid markup that didn't include
    // `data-stl-section` on the <section> tag.
    const section =
      btn.closest('[data-stl-section]') ||
      btn.closest('.iurop-stl');
    if (!section) return;
    // Ensure the data attribute is set so initSection's guards still work.
    if (!section.hasAttribute('data-stl-section')) {
      section.setAttribute('data-stl-section', '');
    }
    if (section.dataset.stlInit !== '1') {
      try { initSection(section); } catch (err) {
        console.error('[iurop-stl] init failed', err);
      }
    }
    if (typeof section.__stlOpen !== 'function') return;
    lastTriggerAt = now;
    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    e.stopPropagation();
    section.__stlOpen(btn.getAttribute('data-stl-open'));
  };

  // Primary trigger: pointerdown (mouse + touch + pen). Fires before click,
  // so even if a third-party app preventDefaults the subsequent click, the
  // modal is already opening.
  window.addEventListener('pointerdown', (e) => {
    if (e.button !== undefined && e.button !== 0) return; // left button / primary touch only
    const btn = e.target && e.target.closest && e.target.closest('[data-stl-open]');
    if (!btn) return;
    triggerOpen(e, btn);
  }, true);

  // Fallback: click (covers cases where pointerdown isn't fired — older
  // browsers, programmatic .click(), unusual input devices).
  window.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest && e.target.closest('[data-stl-open]');
    if (!btn) return;
    triggerOpen(e, btn);
  }, true);

  // Keyboard a11y: Enter / Space on a focused CTA.
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.code !== 'Space') return;
    const btn = e.target && e.target.closest && e.target.closest('[data-stl-open]');
    if (!btn) return;
    triggerOpen(e, btn);
  }, true);
})();
